<div class="nwr-header">
  <h2 mat-dialog-title class="nwr-title">New Workflow Request</h2>
  <div class="requestor-chip" [attr.title]="requestorEmail()">
    <div class="avatar">{{ requestorInitial() }}</div>
    <div class="requestor-meta">
      <div class="requestor-line-1">Submitted by {{ requestorName() || requestorEmail() || 'you' }}</div>
      <div class="requestor-line-2">just now</div>
    </div>
  </div>
</div>

<mat-dialog-content class="nwr-content">

  @if (view() === 'idle') {
    <div class="idle-pane">
      <p class="idle-headline">Drop a document here. We&rsquo;ll figure out what it is.</p>
      <app-file-drop-zone (fileSelected)="onFileSelected($event)"></app-file-drop-zone>
      <div class="idle-link-row">
        <a href="javascript:void(0)" (click)="openChatbot()">No file? Describe it instead &rarr;</a>
      </div>
    </div>
  }

  @if (view() === 'thinking' || view() === 'decision' || view() === 'failed') {
    <div class="reveal-pane">
      <div class="reveal-left">
        @if (selectedFile()?.type === 'application/pdf') {
          <app-pdf-thumbnail [file]="selectedFile()" [displayWidth]="180"></app-pdf-thumbnail>
        } @else {
          <mat-icon class="big-pdf-icon">description</mat-icon>
        }
        <div class="file-name">{{ selectedFile()?.name }}</div>
        <div class="file-size">{{ ((selectedFile()?.size || 0) / 1024).toFixed(1) }} KB</div>
        <button mat-stroked-button class="view-file-btn" (click)="viewFile()">
          <mat-icon>visibility</mat-icon>
          View File
        </button>
      </div>
      <div class="reveal-right">
        <ul class="thinking-list">
          @for (line of thinkingLines(); track $index) {
            <li class="thinking-line">
              <mat-icon class="bullet">chevron_right</mat-icon>
              <span>{{ line }}</span>
            </li>
          }
          @if (view() === 'thinking') {
            <li class="thinking-line thinking-spinner">
              <mat-icon class="bullet spinning">sync</mat-icon>
              <span>Analyzing…</span>
            </li>
          }
        </ul>

        @if (view() === 'failed') {
          <div class="decision-card failure-card">
            <div class="decision-headline failure-headline">
              <mat-icon class="failure-icon">error_outline</mat-icon>
              {{ failureMessage() }}
            </div>

            @if (canChooseManually()) {
              <div class="decision-foot">
                @if (mode === 'workflow') {
                  You can still submit it. What kind of document is this?
                } @else {
                  You can still continue and fill in the details yourself. What kind of document is this?
                }
              </div>
              <div class="manual-type-options">
                @for (opt of manualTypeOptions; track opt.docType) {
                  <button mat-stroked-button [disabled]="workflowSaving()" (click)="chooseTypeManually(opt.docType)">
                    {{ opt.label }}
                  </button>
                }
              </div>
            }

            <div class="decision-secondary-row">
              @if (failureCanRetry()) {
                <a href="javascript:void(0)" class="start-over-link" (click)="retryAnalysis()">Try again</a>
              }
              <a href="javascript:void(0)" class="start-over-link" (click)="startOver()">Start over</a>
              @if (failureReference()) {
                <span class="failure-reference">Reference: {{ failureReference() }}</span>
              }
            </div>
          </div>
        }

        @if (view() === 'decision' && decisionCard(); as card) {
          <div class="decision-card">
            <div class="decision-headline">
              {{ card.headline }}
              @if (card.confidence > 0) {
                <span class="confidence-pill" [class.high]="card.confidence >= 0.9" [class.medium]="card.confidence >= 0.7 && card.confidence < 0.9" [class.low]="card.confidence < 0.7">
                  {{ (card.confidence * 100).toFixed(0) }}% confident
                </span>
              }
            </div>

            @if (card.reasoning) {
              <div class="decision-reasoning">{{ card.reasoning }}</div>
            }

            @if (card.parties.length > 0) {
              <div class="decision-section-label">Parties</div>
              <ul class="parties-list">
                @for (p of card.parties; track p.name) {
                  <li>
                    <mat-icon class="party-icon" [class.matched]="p.matched" [class.unmatched]="!p.matched">
                      {{ p.matched ? 'check_circle' : 'warning' }}
                    </mat-icon>
                    <span class="party-name">{{ p.name }}</span>
                    <span class="party-tag" [class.matched]="p.matched" [class.unmatched]="!p.matched">
                      {{ p.matched ? 'matched' : 'new' }}
                    </span>
                  </li>
                }
              </ul>
            }

            @if (card.effectiveDate || card.expirationDate) {
              <div class="decision-dates">
                @if (card.effectiveDate) { Effective {{ card.effectiveDate }} }
                @if (card.effectiveDate && card.expirationDate) { &middot; }
                @if (card.expirationDate) { Expires {{ card.expirationDate }} }
              </div>
            }

            @if (card.details.length > 0) {
              <div class="decision-details">
                @for (detail of card.details; track $index) {
                  <div class="detail-line">{{ detail }}</div>
                }
              </div>
            }

            @if (mode === 'workflow') {
              <div class="decision-foot">
                I&rsquo;ll create a workflow request and route it to the right person.
              </div>
            } @else if (card.noIntakeAvailable) {
              <div class="no-intake-message">
                <mat-icon>info_outline</mat-icon>
                <span>{{ card.noIntakeMessage }}</span>
              </div>
            } @else {
              <div class="decision-foot">
                I&rsquo;ll {{ card.destinationLabel.toLowerCase() }} with these fields filled in. You can edit anything.
              </div>
            }

            <div class="decision-actions">
              @if (mode === 'workflow') {
                <button mat-raised-button color="primary" [disabled]="workflowSaving()" (click)="acceptRecommendation()">
                  @if (workflowSaving()) { Submitting… } @else { Submit }
                </button>
              } @else if (!card.noIntakeAvailable) {
                <button mat-raised-button color="primary" (click)="acceptRecommendation()">
                  Looks right &mdash; {{ card.destinationLabel }} &rarr;
                </button>
              }
              <button mat-stroked-button (click)="notWhatIExpected()">
                Not what I expected
              </button>
            </div>

            <div class="decision-secondary-row">
              <a href="javascript:void(0)" class="start-over-link" (click)="startOver()">Start over</a>
            </div>
          </div>
        }
      </div>
    </div>
  }

  @if (view() === 'chatbot') {
    <section class="chatbot-panel">
      <div class="chatbot-messages" #chatMessagesContainer>
        @for (msg of chatMessages(); track $index) {
          <div class="chatbot-bubble" [class.bot]="msg.role === 'bot'" [class.user]="msg.role === 'user'">
            {{ msg.content }}
          </div>
        }
        @if (chatLoading()) {
          <div class="chatbot-bubble bot typing">
            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          </div>
        }
      </div>
      <div class="chatbot-input-row">
        <input type="text"
               class="chatbot-input"
               placeholder="Ask a question about your document..."
               [ngModel]="chatInput()"
               (ngModelChange)="chatInput.set($event)"
               (keydown.enter)="sendChatMessage()"
               [disabled]="chatLoading()">
        <button mat-raised-button color="primary" (click)="sendChatMessage()" [disabled]="chatLoading() || !chatInput().trim()">
          <mat-icon>send</mat-icon>
        </button>
      </div>
      <div class="chatbot-back">
        <a href="javascript:void(0)" (click)="backFromChatbot()">&larr; Back</a>
      </div>
    </section>
  }

</mat-dialog-content>

@if (view() === 'idle' || view() === 'chatbot') {
  <mat-dialog-actions align="end">
    <button mat-button (click)="onCancel()">Cancel</button>
  </mat-dialog-actions>
}
